import React, { useCallback, useEffect, useState } from 'react';
import MuiTextField from '@material-ui/core/TextField';
import MuiAutocomplete from '@material-ui/lab/Autocomplete';
import { Chip } from '@material-ui/core';
import { FieldValidation } from '@rjsf/utils';
import {
  CustomFieldExtensionSchema,
  FieldExtensionComponentProps,
} from '@backstage/plugin-scaffolder-react';
import { useApi } from '@backstage/frontend-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const DEFAULT_ALLOWED_DOMAINS = ['chanzuckerberg.com', 'czbiohub.org', 'biohub.org'];

function validateEmail(email: string, allowedDomains: string[]): string | null {
  if (!EMAIL_REGEX.test(email)) {
    return `"${email}" is not a valid email address`;
  }
  const domain = email.split('@')[1]?.toLowerCase();
  if (allowedDomains.length > 0 && !allowedDomains.includes(domain)) {
    return `"${email}" must use one of: ${allowedDomains.join(', ')}`;
  }
  return null;
}

export function MultiEmailPicker({
  onChange,
  rawErrors,
  required,
  formData,
  schema: { title, description },
  uiSchema,
}: FieldExtensionComponentProps<string[]>): JSX.Element {
  const catalogApi = useApi(catalogApiRef);
  const emails = formData ?? [];
  const allowedDomains = (uiSchema?.['ui:options']?.allowedDomains as string[]) ?? DEFAULT_ALLOWED_DOMAINS;
  const personalEmailPolicy = uiSchema?.['ui:options']?.personalEmailPolicy as 'warn' | 'block' | undefined;

  const [inputError, setInputError] = useState('');
  const [warning, setWarning] = useState('');
  const [knownPersonalEmails, setKnownPersonalEmails] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!personalEmailPolicy) return;
    let cancelled = false;
    (async (): Promise<void> => {
      try {
        const result = await catalogApi.getEntities({ filter: { kind: 'User' } });
        const localParts = new Set<string>();
        for (const entity of result.items) {
          const email = (entity.spec as any)?.profile?.email;
          if (email) {
            const localPart = email.split('@')[0]?.toLowerCase();
            if (localPart) localParts.add(localPart);
          }
        }
        if (!cancelled) setKnownPersonalEmails(localParts);
      } catch {
        // Catalog unavailable — skip personal email checks
      }
    })();
    return (): void => { cancelled = true; };
  }, [catalogApi, personalEmailPolicy]);

  // Check all current emails for personal-email warnings
  const checkPersonalEmails = useCallback(
    (emailList: string[]) => {
      if (!personalEmailPolicy || knownPersonalEmails.size === 0) {
        setWarning('');
        return;
      }
      const personal = emailList.filter(e => {
        const localPart = e.split('@')[0]?.toLowerCase();
        return localPart && knownPersonalEmails.has(localPart);
      });
      if (personal.length > 0) {
        setWarning(
          `${personal.join(', ')} — looks like a personal email. Consider using a group alias instead.`,
        );
      } else {
        setWarning('');
      }
    },
    [personalEmailPolicy, knownPersonalEmails],
  );

  const handleChange = useCallback(
    (_event: any, value: any) => {
      const flat = (value as any[]).map((v: any) => String(v).trim()).filter(Boolean);

      if (flat.length > emails.length) {
        const newest = flat[flat.length - 1];

        // Domain / format validation (always blocks)
        const error = validateEmail(newest, allowedDomains);
        if (error) {
          setInputError(error);
          return;
        }

        // Personal email check
        if (personalEmailPolicy && knownPersonalEmails.has(newest.split('@')[0]?.toLowerCase())) {
          if (personalEmailPolicy === 'block') {
            setInputError(`"${newest}" is a personal email — please use a group alias`);
            return;
          }
          // 'warn' — allow but show warning after update
        }
      }

      setInputError('');
      onChange(flat);
      checkPersonalEmails(flat);
    },
    [onChange, emails.length, allowedDomains, personalEmailPolicy, knownPersonalEmails, checkPersonalEmails],
  );

  const hasWarning = !!warning && !inputError;
  const helperText = inputError || (hasWarning ? warning : description) || 'Type an email and press Enter';

  return (
    <MuiAutocomplete
      multiple
      freeSolo
      autoSelect
      value={emails}
      options={[]}
      onChange={handleChange}
      renderTags={(value, getTagProps) =>
        value.map((email, index) => {
          const { key: _key, ...tagProps } = getTagProps({ index }) as any;
          return <Chip label={email} size="small" key={email} {...tagProps} />;
        })
      }
      renderInput={params => (
        <MuiTextField
          {...params}
          label={title}
          helperText={helperText}
          margin="dense"
          variant="outlined"
          required={required}
          error={!!inputError || !!rawErrors?.length}
          FormHelperTextProps={hasWarning ? { style: { color: '#e67e22' } } : undefined}
        />
      )}
    />
  );
}

export const MultiEmailPickerSchema: CustomFieldExtensionSchema = {
  uiOptions: {
    type: 'object',
    properties: {
      allowedDomains: {
        type: 'array',
        items: { type: 'string' },
        title: 'Allowed email domains (empty = any domain)',
      },
      personalEmailPolicy: {
        type: 'string',
        title: 'How to handle personal (individual) emails: "warn" shows amber warning, "block" rejects entry. Omit to disable.',
        enum: ['warn', 'block'],
      },
    },
  },
  returnValue: {
    type: 'array',
    items: { type: 'string' },
    title: 'Array of email addresses',
  },
};

export const MultiEmailPickerValidation = (
  formData: string[],
  validation: FieldValidation,
): void => {
  if (!formData || formData.length === 0) {
    return;
  }
  for (const email of formData) {
    if (!EMAIL_REGEX.test(email)) {
      validation.addError(`"${email}" is not a valid email address.`);
    }
  }
};
